/* eslint-disable @typescript-eslint/no-magic-numbers */
import React, { useMemo } from 'react';
import { Stack } from '@cp/ds/src/components/stack';
import { coreCatalogItemApi } from '@cp/entities/catalogItem';
import { Skeleton } from '@cp/ds/src/components/skeleton';
import { CatalogItemType } from '@common/model/catalog/item/type';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { MOBILE_BREAKPOINT_THRESHOLD } from '@cp/ds/src/theme';
import { ButtonProps } from '@cp/ds/src/components/button/model';
import { mergeSx } from '@cp/ds/src/utils/sx/merge';
import { Button } from '@cp/ds/src/components/button';
import { clientTypeToSuitableCatalogItemType } from '@common/utils/catalogType';
import { useGetPromocodes } from '@cp/entities/promocode';
import { useIsLoggedIn } from '@cp/entities/currentUser';
import { ClientType } from '@common/model/client/clientType';
import { useClientProfile } from 'src/entities/client';
import { useCheckPatronageCompleted, useShowPatronageHints } from 'src/features/client/current/patronage';
import { RespondToVacancyDeps, ResponseButton } from 'src/features/catalogItem/response';
import { withSubscriptionTooltip } from 'src/features/billing';
import { useTrackEvent, withTrackClick } from 'src/shared/tracking';
import { CatalogEvents } from 'src/features/catalog';
import { mapCatalogItemToClientSelection, useAuth } from 'src/features/auth';
import { useConnectAction } from './useConnectAction';
import { CatalogItemActionsProps } from './model';
import { Favorite } from './favorite';

export * from './model';

const ResponseButtonWithSubscription = withSubscriptionTooltip(ResponseButton);
const InviteButton = withTrackClick(Button, CatalogEvents.ItemInvite);

export const CatalogItemActions: React.FC<CatalogItemActionsProps> = ({ id, type: typeFromProps, sx }) => {
  const track = useTrackEvent();
  const theme = useTheme();
  const clientProfile = useClientProfile();
  const auth = useAuth();
  const { data: catalogItem, isLoading } = coreCatalogItemApi.endpoints.getById.useQuery(
    { id: id as NonNullable<typeof id> },
    { skip: !id },
  );
  const loggedIn = useIsLoggedIn();
  const promo = useGetPromocodes();
  const clientWithPromo = !loggedIn || promo.length > 0;

  const type = typeFromProps ?? catalogItem?.type ?? CatalogItemType.Resume;
  const isResume = type === CatalogItemType.Resume;
  const [connectAction] = useConnectAction(catalogItem);

  const buttonsProps: ButtonProps = {
    sx: isResume ? {} : { flex: { xs: '1 1 50%', [MOBILE_BREAKPOINT_THRESHOLD]: 'initial' }, whiteSpace: 'nowrap' },
    disabled: clientProfile?.type ? clientTypeToSuitableCatalogItemType(clientProfile.type) !== type : false,
  };

  const checkPatronageCompleted = useCheckPatronageCompleted();
  const showPatronageHints = useShowPatronageHints();
  const respondToVacancyDeps = useMemo(
    (): RespondToVacancyDeps => ({
      onNeedLogin: () => (catalogItem ? auth({ careerId: catalogItem.careerId, clientType: ClientType.Specialist }) : auth()),
      patronage: {
        check: checkPatronageCompleted,
        action: showPatronageHints,
      },
    }),
    [auth, checkPatronageCompleted, showPatronageHints, catalogItem],
  );

  let content: React.ReactNode = null;

  if (isLoading) {
    content = (
      <>
        <Skeleton height={40} width={120} />
        <Skeleton height={40} width={120} />
      </>
    );
  } else if (catalogItem) {
    content = isResume ? (
      <>
        <InviteButton
          onClick={connectAction}
          {...buttonsProps}
          sx={mergeSx(buttonsProps.sx, { flex: { xs: '1 1 0px', [MOBILE_BREAKPOINT_THRESHOLD]: 'initial' } })}
        >
          Связаться {clientWithPromo ? 'бесплатно' : ''}
        </InviteButton>
        {catalogItem.clientProfile && (
          <Favorite
            clientSelection={mapCatalogItemToClientSelection(catalogItem)}
            color="gray ultra light"
            {...buttonsProps}
            author={catalogItem.clientProfile}
          />
        )}
      </>
    ) : (
      <>
        <ResponseButtonWithSubscription
          {...buttonsProps}
          deps={respondToVacancyDeps}
          onClickCapture={() => track(CatalogEvents.ItemResponse())}
          request={{ catalogItem, responder: clientProfile }}
          sx={buttonsProps.sx}
          tooltipText="Чтобы откликаться и видеть контакты необходимо"
        >
          Я могу
        </ResponseButtonWithSubscription>
        <ResponseButtonWithSubscription
          {...buttonsProps}
          color="gray"
          deps={respondToVacancyDeps}
          onClickCapture={() => track(CatalogEvents.ItemQuestion())}
          request={{ catalogItem, responder: clientProfile }}
          sx={mergeSx(buttonsProps.sx, { backgroundColor: theme.palette.white })}
          tooltipText="Чтобы откликаться и видеть контакты необходимо"
          variant="secondary"
        >
          Задать вопрос
        </ResponseButtonWithSubscription>
      </>
    );
  }

  if (!content) {
    return null;
  }

  return (
    <Stack alignItems="center" direction="row" spacing={1.5} sx={sx}>
      {content}
    </Stack>
  );
};
